import React, { useEffect, useState } from 'react';
import { Breadcrumbs } from '@wikifactory/voxel-ui';
import Skeleton from 'components/Skeleton';
import { Workspace } from 'types';
import { capitalize, getMapRegExp, isSystemSlug } from 'utils';
import { AvatarProps } from 'components/Avatar/Avatar';
import {
  RFQQuoteOrderDetailRoutes,
  getSystemCondition,
  needAddWorkspaceRoutes,
  spaceProjectRoutes,
} from 'apps/routes/utils';
import { communityRoutes } from 'apps/components/Frame/config';
import { graphqlIdToDecimal } from 'utils/dgraphId';
import { routerStore } from 'core/routing';
import {
  isInDesktopMode,
  setCurrentTabHoverTitle,
} from 'libs/gutClient/electronBridge';

import { observer } from 'mobx-react';
import { useCrumbItemsLoading } from 'hooks';
import { clientStore } from 'apps/client/app';
import { encodeParams } from 'apps/routes/routesParamsEncoding';
import { PortalLink } from 'apps/client/components/PortalLink';
import { analytics } from 'core/analytics';
import { frameStore } from '../../appManager';

const sharedWithMeText = 'Shared with me';
const shareWithMePrefix = 'shared-with-';

export type ActiveInfoProps = {
  avatarSize?: AvatarProps['size'];
};

export const SubHeaderBreadcrumbs = observer(() => {
  const { crumbItemsLoading } = useCrumbItemsLoading();

  const { route } = routerStore;

  const {
    signedInProfile,
    currentSpaceData: activeSpaceCheckRawInfo,
    workspaces,
  } = clientStore;

  const profileSlug = signedInProfile?.username;

  const [lastSlug, setLastSlug] = useState(null);
  const [currentSpaceData, setCurrentSpaceData] = useState(
    activeSpaceCheckRawInfo,
  );

  useEffect(() => {
    if (clientStore.slugFromRouteOrSpace !== lastSlug || !currentSpaceData) {
      setCurrentSpaceData(activeSpaceCheckRawInfo);
      setLastSlug(clientStore.slugFromRouteOrSpace);
    }
  }, [lastSlug, activeSpaceCheckRawInfo, clientStore.slugFromRouteOrSpace]);

  useEffect(() => {
    if (lastSlug) {
      analytics.linkAmplitudeGroup(lastSlug);
    }
  }, [lastSlug]);

  const atCommunityRoute =
    !clientStore.slugFromRouteOrSpace ||
    !['shared.file', '.project'].some(r => route.name.includes(r)) ||
    communityRoutes.includes(route.name) ||
    !workspaces?.length ||
    clientStore.atCommunityRoute;

  const isSharedWithCurrentSpace = () => {
    const routeContentSlug = clientStore.slugFromRouteOrSpace?.substring(1);
    const isInWorkSpace =
      workspaces &&
      workspaces?.find((space: Workspace) => {
        return space?.content.slug === routeContentSlug;
      });

    if (isInWorkSpace || atCommunityRoute) {
      return false;
    }

    return true;
  };

  const isSharedWithMe = isSharedWithCurrentSpace();

  const currentRouteSpaceName = currentSpaceData?.content?.title || '';

  const { file, project, quote } = frameStore;
  const currentFileName = file?.filename || file?.name;
  const { shouldShowIntermediatorSwitcher } = getSystemCondition();

  const {
    profile,
    initiative,
    spacePath,
    sharedFileId,
    rfqId,
    quoteId,
    slug: projectSlug,
    customerSlug,
    contributionId,
    ...restRouteParams
  } = route.params;

  useEffect(() => {
    if (!projectSlug) {
      frameStore.SetProject(null);
    }
  }, [projectSlug]);

  const slugFromRouteOrSpace = profile || initiative || spacePath;

  const isInIntermediatorMode = clientStore.currentPortal === 'INTERMEDIATOR';

  const isInPublicProjectPage =
    project && !project.private && !spaceProjectRoutes.includes(route.name);

  const encodedProjectSlug = encodeParams({ slug: projectSlug }).slug;
  const path = window.location.pathname
    .replace('/order', '/orders')
    .replace(`/v/${contributionId}`, '')
    .replace(`/${encodedProjectSlug}/file/`, `/${encodedProjectSlug}/files/`);

  const currentSlug = clientStore.slugFromRouteOrSpace;

  const hiddenPathMap: Record<string, any> = {
    [contributionId ? `/v/${contributionId}` : '']: null,
  };

  const displayPaths = path.split('/').filter(Boolean);

  const rfqID = Number.isFinite(rfqId)
    ? rfqId
    : rfqId && graphqlIdToDecimal(rfqId);

  const rfqCrumbText =
    currentFileName && rfqId && Number.isFinite(rfqID)
      ? `${rfqID}/${currentFileName}`
      : currentFileName || rfqID;

  const regGroupMap: Record<string, string> = {
    '/rfq': '/RFQ',
    '/rfqs': '/RFQs',
    '/notifications': '/Your Notification',
    '/make-part': '/New RFQ',
    '/upload-file': '/Upload File',
    '/new-quote': '/New Quote',
    '/settings/collaborators': '/settings',
    '/settings/general': '/settings',
    '/settings/admin': '/settings',
    '/settings/fileSync': '/settings',
    ...hiddenPathMap,
    [sharedFileId]: currentFileName || sharedFileId,
    [quoteId]: quote?.decimalId ?? quoteId,
    [rfqId]: rfqCrumbText,
  };

  const getSlugDisplayName = () => {
    if (isInPublicProjectPage) {
      return 'Community';
    }

    return isSystemSlug(slugFromRouteOrSpace)
      ? currentRouteSpaceName
      : slugFromRouteOrSpace;
  };

  const encodedSlugFromRouteOrSpace = encodeParams({
    spacePath: slugFromRouteOrSpace,
  }).spacePath;

  const plainPathMap: Record<string, string> = {
    [slugFromRouteOrSpace]: getSlugDisplayName(),
    [encodedSlugFromRouteOrSpace]: getSlugDisplayName(),
    [projectSlug]: project?.name || projectSlug,
    // [encodedProjectSlug]: project?.name || projectSlug,
    [customerSlug]: customerSlug,
  };

  const matchRegExp = getMapRegExp(regGroupMap);

  const prefixPaths =
    clientStore.slugFromRouteOrSpace && isSharedWithMe
      ? [sharedWithMeText]
      : [];

  const shouldNotCapitalizeList = Object.values(restRouteParams)
    .concat([
      projectSlug,
      customerSlug,
      project?.name,
      rfqCrumbText,
      currentRouteSpaceName,
      currentFileName,
    ])
    .filter(Boolean)
    .map(String);

  const inSpaceProject = spaceProjectRoutes.includes(route.name);

  const unformattedCrumbItems = path
    .replace(matchRegExp, (substring: string, key: string) => {
      return regGroupMap[key] || substring;
    })
    .replace(
      `${encodedSlugFromRouteOrSpace}/${encodedProjectSlug}`,
      `${encodedSlugFromRouteOrSpace}/projects/${encodedProjectSlug}`,
    )
    .split('/');

  const crumbItems = prefixPaths
    .concat(
      unformattedCrumbItems.map(key => {
        const text = plainPathMap[key] || key;

        const shouldKeepOriginText = shouldNotCapitalizeList.some(keepPath =>
          keepPath?.includes(text),
        );
        if (shouldKeepOriginText) {
          return text;
        }

        return capitalize(text);
      }),
    )
    .filter(Boolean);

  const homePrefix = `workspace/${currentSlug}`;

  const getCommunityProjectPath = ({
    paths,
    subPath,
  }: {
    paths: string[];
    subPath: string;
  }) => {
    const [firstPath, ...rest] = paths;

    const communityMap: Record<string, string> = {
      Community: 'community/featured',
      Projects: 'community/projects',
    };

    const result =
      isInPublicProjectPage && communityMap[subPath]
        ? communityMap[subPath]
        : [`workspace/${firstPath}`, ...rest].join('/');

    return result;
  };

  const getNavigationPath = (
    paths: string[],
    options: { isInSpaceLocation: boolean; currentIndex: number },
  ) => {
    const { isInSpaceLocation, currentIndex } = options;
    const [firstPath, ...rest] = paths;
    let navigateFirstPath = firstPath;

    if (isInSpaceLocation) {
      const isInRFQsOrQuotesOrOrdersRoutes = RFQQuoteOrderDetailRoutes.includes(
        route.name,
      );

      const isInProjectRoues = spaceProjectRoutes.includes(route.name);

      if (isInRFQsOrQuotesOrOrdersRoutes) {
        navigateFirstPath = isInIntermediatorMode
          ? homePrefix
          : `${homePrefix}/${firstPath}`;
      } else if (isInProjectRoues) {
        const ProjectSpaceMap: Record<string, string> = {
          0: homePrefix,
        };

        navigateFirstPath =
          ProjectSpaceMap[currentIndex] || `workspace/${firstPath}/projects`;
      } else {
        navigateFirstPath = `workspace/${firstPath}`;
      }
    }

    return [navigateFirstPath, ...rest].join('/');
  };

  const getSharePath = (
    subPath: string,
    {
      needMap,
      currentPathList,
    }: { needMap: boolean; currentPathList: string[] },
  ) => {
    const isInProjectRoues = spaceProjectRoutes.includes(route.name);

    const sharedProjectsRoute = `workspace/${profileSlug}/${shareWithMePrefix}projects`;
    const sharedFilesRoute = `workspace/${profileSlug}/${shareWithMePrefix}files`;

    const sharedHomeRoute = isInProjectRoues
      ? sharedProjectsRoute
      : sharedFilesRoute;

    const shareWithMeMap: Record<string, string> = {
      [sharedWithMeText]: sharedHomeRoute,
      [currentRouteSpaceName]: sharedHomeRoute,
      Projects: sharedProjectsRoute,
      Files: sharedFilesRoute,
    };

    return needMap ? shareWithMeMap[subPath] : currentPathList.join('/');
  };

  useEffect(() => {
    if (isInDesktopMode()) {
      setCurrentTabHoverTitle(crumbItems.join('/'));
    }
  }, [isInDesktopMode, crumbItems]);

  if (crumbItemsLoading) {
    return (
      <div className='w-28 mx-2'>
        <Skeleton variant='rounded' width='100%' height={20} />
      </div>
    );
  }

  if (!crumbItems?.length) return null;

  return (
    <Breadcrumbs style={{ margin: '0 10px' }}>
      {crumbItems.map((item, index, list) => {
        const isLast = list.length - 1 === index;
        const segmentsNumOfSharedWithMe = 2;

        const currentPathList = displayPaths.slice(
          0,
          // On the project space page(inSpaceProject is true), we manually added the Projects item to the bread crumb item, so the index slice can go to the previous one.
          inSpaceProject && !isSharedWithMe ? index : index + 1,
        );
        const isInSpaceSegments = isSharedWithMe
          ? index <= segmentsNumOfSharedWithMe
          : index <= 1;

        const getJumpPath = () => {
          if (isSharedWithMe) {
            return getSharePath(item, {
              needMap: index <= segmentsNumOfSharedWithMe,
              currentPathList: displayPaths.slice(
                0,
                // Share with me also add 'Project' item for bread crumb
                index > segmentsNumOfSharedWithMe ? index - 1 : index + 1,
              ),
            });
          }

          if (isInPublicProjectPage && isInSpaceSegments) {
            return getCommunityProjectPath({
              paths: currentPathList,
              subPath: item,
            });
          }

          return getNavigationPath(currentPathList, {
            isInSpaceLocation:
              isInSpaceSegments &&
              !clientStore.atCommunityRoute &&
              needAddWorkspaceRoutes.includes(route.name),
            currentIndex: index,
          });
        };

        const jumpPath = getJumpPath();

        const jumpHref = `${window.location.origin}/${jumpPath}`;
        const matchInfo = routerStore.router.matchUrl(jumpHref);
        const { name, params } = matchInfo || {};
        const clickable = matchInfo && !isLast;

        let encodedItem = item;
        try {
          encodedItem = decodeURIComponent(item);
        } catch (err) {
          // This happens when the username contains invalid escape sequences like %C3
          // To really fix this we need to rewrite the logic of this component
          // for now it's just an ugly edge case. Routing works it's just the display of the
          // label
          console.warn(err);
        }
        if (clickable) {
          return (
            <PortalLink
              dataTestId={`${encodedItem}-${jumpHref}`}
              name={name}
              params={{
                ...(params || {}),
                // ...intermediatorModeParams,
              }}
              switchToHomeTab
              className='cursor-pointer text-black hover:underline'
              key={item}
            >
              {encodedItem}
            </PortalLink>
          );
        }

        if (isLast) {
          return (
            <span
              key={item}
              className='font-semibold cursor-default text-black'
            >
              {encodedItem}
            </span>
          );
        }

        // cannot click
        return (
          <span key={item} className='text-black'>
            {encodedItem}
          </span>
        );
      })}
    </Breadcrumbs>
  );
});
